// Right-rail "what you're making" artwork for the configurator spec summary.
// A richer, dimensional successor to FamilyGlyph (which stays the flat picker
// icon): each product family reads as a small 3D-style object — a thick
// paperback, a stapled booklet, a coil book, a punched loose-leaf stack, a
// single sheet, a label sheet, a USB drive, an optical disc, an open kit carton
// and a dust-jacket wrap.
//
// Two modes, one geometry (the "baseline + upgrade" the product owner picked):
//   - BASELINE  — a branded placeholder (currentColor panel) on the product
//                 front, the same for every quote of that type.
//   - UPGRADE   — when the customer has uploaded artwork, that art is painted
//                 onto the SAME front face (à la the diybookcovers 3D mockups):
//                 booklet families wear it on the book cover, single sheet wears
//                 it on the flat sheet (sized to the finished-size aspect ratio),
//                 and a dust jacket wears it across the whole wrap. An
//                 assembly kit has no face to paint, so its finished-kit photo
//                 replaces the carton outright.
//                 The thumbnail is produced by useArtworkPreview and handed in as
//                 `artworkUrl`.
//
// All shading is `currentColor` at varying opacity (the art box sets color to
// the brand accent — see .spec-summary__art), so the icons stay themeable and
// carry no raw hex (house rule). The live cover <image> is the one full-color
// element; it's a same-origin blob object URL, so it's CSP-safe.
//
// Projection is OBLIQUE (parallel), not true perspective: the front face stays
// a rectangle, so a uploaded cover maps onto it with no warping, and depth reads
// from the receding top/right faces. (SVG <image> only supports affine maps —
// a true perspective trapezoid isn't expressible — and oblique looks clean and
// crisp at icon size anyway.)

import { useId, type JSX } from 'react';

import { type FamilyCode } from './FamilyGlyph.js';

// ── shared geometry ──────────────────────────────────────────────────────
// Front cover rectangle, shared by every booklet family so the baseline and
// the live-cover upgrade sit in exactly the same place.
const FX = 25; // front-left
const FY = 14; // front-top
const FW = 58; // cover width (≈ a 6×9 portrait book)
const FH = 88; // cover height
const FX2 = FX + FW; // front-right
const FY2 = FY + FH; // front-bottom

// Oblique depth → the receding offset for the top + fore-edge (pages) faces.
// dx right, dy up. Per-family thickness drives how chunky the book reads — kept
// small so the result is a BOOK (thin page stack visible on the right), not a
// cube with a cover pasted on the front.
function offset(depth: number): { ox: number; oy: number } {
  return { ox: depth, oy: -depth * 0.55 };
}

function pts(coords: [number, number][]): string {
  return coords.map(([x, y]) => `${x},${y}`).join(' ');
}

type BookBinding = 'perfect' | 'saddle' | 'coil' | 'loose';

const BINDING_DEPTH: Record<BookBinding, number> = {
  perfect: 7,
  coil: 6,
  loose: 5,
  saddle: 3,
};

// ── the book ───────────────────────────────────────────────────────────────
// A standing book seen slightly from the front-right: the front cover faces us,
// a thin top edge and a light page fore-edge recede up-right, and a soft spine
// fold runs down the left where the cover wraps. The thin, densely-lined
// fore-edge is what reads as "a stack of paper" (a book) rather than a solid
// cube side (a box).
function Book({
  binding,
  coverImageUrl,
}: {
  binding: BookBinding;
  coverImageUrl?: string | null;
}): JSX.Element {
  const base = useId();
  const clipId = `${base}-cover`;
  const spineId = `${base}-spine`;
  const shadowId = `${base}-shadow`;
  const { ox, oy } = offset(BINDING_DEPTH[binding]);
  const hasCover = Boolean(coverImageUrl);

  // Page-edge lines marching up-right across the fore-edge — a few fine lines so
  // the right side reads as cut pages (a slim book), not a thick block.
  const PAGE_LINES = 4;
  const pageLines = Array.from({ length: PAGE_LINES }, (_, i) => {
    const t = (i + 1) / (PAGE_LINES + 1);
    return (
      <line
        key={i}
        x1={FX2 + ox * t}
        y1={FY + oy * t + 4}
        x2={FX2 + ox * t}
        y2={FY2 + oy * t - 4}
        stroke="currentColor"
        strokeWidth="0.8"
        opacity="0.15"
      />
    );
  });

  return (
    <svg viewBox="0 0 120 124" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id={clipId}>
          <rect x={FX} y={FY} width={FW} height={FH} rx="1.5" />
        </clipPath>
        {/* spine fold — a soft darkening down the left edge where the cover wraps */}
        <linearGradient id={spineId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
        {/* soft, feathered ground shadow */}
        <radialGradient id={shadowId}>
          <stop offset="0" stopColor="currentColor" stopOpacity="0.2" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </radialGradient>
      </defs>

      <ellipse cx={FX + FW / 2 + 2} cy={FY2 + 8} rx={FW * 0.58} ry="6.5" fill={`url(#${shadowId})`} />

      {/* light page fore-edge (the right face) + stacked page lines */}
      <polygon
        points={pts([
          [FX2, FY],
          [FX2 + ox, FY + oy],
          [FX2 + ox, FY2 + oy],
          [FX2, FY2],
        ])}
        fill="currentColor"
        opacity="0.05"
      />
      {pageLines}

      {/* thin top edge of the cover (receding up-right) */}
      <polygon
        points={pts([
          [FX, FY],
          [FX2, FY],
          [FX2 + ox, FY + oy],
          [FX + ox, FY + oy],
        ])}
        fill="currentColor"
        opacity="0.22"
      />

      {/* front cover — live art when supplied, else a branded placeholder */}
      {hasCover ? (
        <image
          href={coverImageUrl as string}
          x={FX}
          y={FY}
          width={FW}
          height={FH}
          // Anchor RIGHT, not center: a cover upload is a full wrap (back | spine
          // | front, left→right — see COMPONENT_ROLE_HELP.cover), so the front
          // panel is the right edge. Right-anchored slice shows the front; it's
          // harmless for a front-only portrait cover (negligible left crop).
          preserveAspectRatio="xMaxYMid slice"
          clipPath={`url(#${clipId})`}
        />
      ) : (
        <>
          <rect x={FX} y={FY} width={FW} height={FH} rx="1.5" fill="currentColor" opacity="0.1" />
          {/* faint cover furniture — an emblem + two title bars, no text */}
          <circle cx={FX + FW / 2} cy={FY + 30} r="9" fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.28" />
          <rect x={FX + 13} y={FY + 50} width={FW - 26} height="4" rx="2" fill="currentColor" opacity="0.24" />
          <rect x={FX + 19} y={FY + 60} width={FW - 38} height="4" rx="2" fill="currentColor" opacity="0.2" />
        </>
      )}

      {/* spine fold shadow over the left edge — sells the cover-wraps-a-spine
          read on both the placeholder and the live cover */}
      <rect x={FX} y={FY} width="11" height={FH} fill={`url(#${spineId})`} clipPath={`url(#${clipId})`} />

      {/* binding cue at the spine */}
      <BindingCue binding={binding} />

      {/* crisp book silhouette + the cover/fore-edge seam */}
      <polygon
        points={pts([
          [FX, FY2],
          [FX, FY],
          [FX + ox, FY + oy],
          [FX2 + ox, FY + oy],
          [FX2 + ox, FY2 + oy],
          [FX2, FY2],
        ])}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
        opacity="0.34"
      />
      <line x1={FX2} y1={FY} x2={FX2} y2={FY2} stroke="currentColor" strokeWidth="1" opacity="0.3" />
    </svg>
  );
}

// Per-binding treatment at the spine (left edge). The soft spine-fold gradient
// is drawn for every book in Book(); this adds the mechanical cue on top.
function BindingCue({ binding }: { binding: BookBinding }): JSX.Element | null {
  if (binding === 'coil') {
    // A wire-o coil straddling the spine.
    return (
      <>
        {[0, 1, 2, 3, 4, 5, 6].map((i) => {
          const cy = FY + 9 + i * ((FH - 18) / 6);
          return (
            // Centered just LEFT of the cover edge so the coil straddles the
            // spine rather than reading as rings punched through the art.
            <ellipse
              key={i}
              cx={FX - 2}
              cy={cy}
              rx="4.5"
              ry="3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              opacity="0.5"
            />
          );
        })}
      </>
    );
  }
  if (binding === 'loose') {
    // Three punched holes — a binder / loose-leaf cue.
    return (
      <>
        {[0.25, 0.5, 0.75].map((t) => (
          // Punched near the binding edge — kept close to the edge so they read
          // as binder holes, not holes in the cover artwork.
          <circle
            key={t}
            cx={FX + 6}
            cy={FY + FH * t}
            r="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            opacity="0.45"
          />
        ))}
      </>
    );
  }
  if (binding === 'saddle') {
    // Two staples on the fold.
    return (
      <>
        {[0.33, 0.66].map((t) => (
          <rect
            key={t}
            x={FX - 1.5}
            y={FY + FH * t - 5}
            width="3"
            height="10"
            rx="1"
            fill="currentColor"
            opacity="0.5"
          />
        ))}
      </>
    );
  }
  // perfect-bound — the spine fold gradient (in Book) is the whole cue.
  return null;
}

// ── flat / non-book families ────────────────────────────────────────────────
// Bounding box the single sheet is fit into. The sheet stands on the same ground
// line as the book (FY2) and is centred on the viewBox, so portrait and
// landscape trims both read as a balanced sheet rather than drifting.
const SHEET_MAX_W = 66;
const SHEET_MAX_H = FH; // 88 — same standing height as the book cover
const SHEET_CX = 60; // viewBox centre
const SHEET_BASELINE = FY2; // bottom edge sits on the book's ground line
// The look before a finished size is picked: the former fixed portrait sheet.
const SHEET_DEFAULT_ASPECT = FW / FH; // ≈ 0.66

// Fit a sheet of the given aspect (width / height) into the bounding box,
// preferring full height and clamping width so a wide trim (e.g. a 3.5″ × 2″
// business card, aspect 1.75) stays inside the frame.
function sheetRect(aspect: number): { x: number; y: number; w: number; h: number } {
  const a = aspect > 0 ? aspect : SHEET_DEFAULT_ASPECT;
  let h = SHEET_MAX_H;
  let w = h * a;
  if (w > SHEET_MAX_W) {
    w = SHEET_MAX_W;
    h = w / a;
  }
  return { x: SHEET_CX - w / 2, y: SHEET_BASELINE - h, w, h };
}

function SingleSheet({
  artworkUrl,
  aspect,
}: {
  // Live flat-artwork thumbnail (from useArtworkPreview). When present the sheet
  // wears the customer's actual file; otherwise it shows the branded placeholder.
  artworkUrl?: string | null;
  // Finished-size aspect ratio (width / height) — drives the sheet's shape so a
  // postcard reads portrait and a business card reads landscape. Defaults to the
  // former fixed portrait look until a size is picked.
  aspect?: number;
}): JSX.Element {
  const base = useId();
  const clipId = `${base}-sheet`;
  const { x, y, w, h } = sheetRect(aspect ?? SHEET_DEFAULT_ASPECT);
  const x2 = x + w;
  const y2 = y + h;
  const hasArt = Boolean(artworkUrl);
  // Dog-ear scales with the sheet so a short card doesn't get a corner bigger
  // than itself.
  const fold = Math.min(14, w * 0.24, h * 0.24);

  return (
    <svg viewBox="0 0 120 124" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id={clipId}>
          <rect x={x} y={y} width={w} height={h} rx="1.5" />
        </clipPath>
      </defs>
      <ellipse cx={SHEET_CX} cy={SHEET_BASELINE + 8} rx={w * 0.58} ry="5.5" fill="currentColor" opacity="0.12" />
      {hasArt ? (
        <>
          {/* Live art painted into the sheet. "slice"-filled like the book cover:
              the rect already carries the finished-size aspect, so a correctly
              sized file fills it with negligible crop. The one full-colour
              element; it's a same-origin blob object URL, so it's CSP-safe. */}
          <image
            href={artworkUrl as string}
            x={x}
            y={y}
            width={w}
            height={h}
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#${clipId})`}
          />
          {/* crisp sheet edge over the art */}
          <rect
            x={x}
            y={y}
            width={w}
            height={h}
            rx="1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            opacity="0.34"
          />
        </>
      ) : (
        <>
          {/* sheet with a folded top-right corner */}
          <path
            d={`M${x} ${y} H${x2 - fold} L${x2} ${y + fold} V${y2} H${x} Z`}
            fill="currentColor"
            opacity="0.1"
          />
          <path
            d={`M${x} ${y} H${x2 - fold} L${x2} ${y + fold} V${y2} H${x} Z`}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            opacity="0.34"
          />
          {/* dog-eared corner */}
          <path d={`M${x2 - fold} ${y} V${y + fold} H${x2} Z`} fill="currentColor" opacity="0.22" />
          {/* text lines — placed proportionally so they track the sheet's size */}
          {[0.24, 0.37, 0.5, 0.63].map((t, i) => (
            <rect
              key={t}
              x={x + w * 0.16}
              y={y + h * t}
              width={w * (i === 3 ? 0.42 : 0.68)}
              height={Math.max(3, h * 0.045)}
              rx="2"
              fill="currentColor"
              opacity="0.2"
            />
          ))}
        </>
      )}
    </svg>
  );
}

function LabelSheet(): JSX.Element {
  const cols = 2;
  const rows = 3;
  const pad = 9;
  const gap = 7;
  const cw = (FW - pad * 2 - gap * (cols - 1)) / cols;
  const ch = (FH - pad * 2 - gap * (rows - 1)) / rows;
  const labels: JSX.Element[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      labels.push(
        <rect
          key={`${r}-${c}`}
          x={FX + pad + c * (cw + gap)}
          y={FY + pad + r * (ch + gap)}
          width={cw}
          height={ch}
          rx="3"
          fill="currentColor"
          opacity="0.2"
        />,
      );
    }
  }
  return (
    <svg viewBox="0 0 120 124" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="60" cy={FY2 + 8} rx="34" ry="5" fill="currentColor" opacity="0.12" />
      <rect x={FX} y={FY} width={FW} height={FH} rx="2.5" fill="currentColor" opacity="0.07" />
      <rect
        x={FX}
        y={FY}
        width={FW}
        height={FH}
        rx="2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        opacity="0.34"
      />
      {labels}
    </svg>
  );
}

function OpticalDisc(): JSX.Element {
  // A disc seen slightly face-on: outer rim, data ring, hub and centre hole,
  // with a soft highlight so it reads as a pressed disc rather than a flat ring.
  return (
    <svg viewBox="0 0 120 124" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="60" cy="90" rx="34" ry="5" fill="currentColor" opacity="0.12" />
      <circle cx="60" cy="54" r="34" fill="currentColor" opacity="0.1" />
      <circle cx="60" cy="54" r="34" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.34" />
      <circle cx="60" cy="54" r="25" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.22" />
      <path
        d="M60 24 A30 30 0 0 1 90 54"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        opacity="0.22"
      />
      <circle cx="60" cy="54" r="11" fill="currentColor" opacity="0.14" />
      <circle cx="60" cy="54" r="11" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.3" />
      <circle cx="60" cy="54" r="4.5" fill="#fff" />
      <circle cx="60" cy="54" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.34" />
    </svg>
  );
}

function UsbDrive(): JSX.Element {
  // A USB stick lying at a slight angle: body + metal connector, with a little
  // dimensional offset so it reads as an object rather than a flat icon.
  return (
    <svg viewBox="0 0 120 124" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      {/* connector */}
      <rect x="24" y="44" width="20" height="20" rx="2" fill="currentColor" opacity="0.28" />
      <rect x="27" y="48" width="5" height="12" rx="1" fill="currentColor" opacity="0.45" />
      <rect x="36" y="48" width="5" height="12" rx="1" fill="currentColor" opacity="0.45" />
      {/* body */}
      <rect x="44" y="38" width="52" height="32" rx="4" fill="currentColor" opacity="0.12" />
      <rect
        x="44"
        y="38"
        width="52"
        height="32"
        rx="4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        opacity="0.34"
      />
      {/* cap seam + indicator */}
      <line x1="58" y1="38" x2="58" y2="70" stroke="currentColor" strokeWidth="1.3" opacity="0.3" />
      <circle cx="88" cy="54" r="2.5" fill="currentColor" opacity="0.4" />
      {/* ground shadow */}
      <ellipse cx="60" cy="84" rx="40" ry="6" fill="currentColor" opacity="0.13" />
    </svg>
  );
}

function KitCarton(): JSX.Element {
  // An open carton being filled: parts standing above the rim, the opening
  // receding up-right on the same oblique projection the book uses, and a kit
  // label on the front face.
  const bx = 26;
  const by = 54;
  const bw = 64;
  const bh = 42;
  const { ox, oy } = offset(12);
  const bx2 = bx + bw;
  const by2 = by + bh;

  return (
    <svg viewBox="0 0 120 124" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx={bx + bw / 2} cy={by2 + 7} rx={bw * 0.56} ry="6" fill="currentColor" opacity="0.13" />

      {/* the gathered parts, standing in the box (drawn first so the carton
          faces overlap their lower ends) */}
      <rect x="40" y="22" width="22" height="30" rx="1.5" fill="currentColor" opacity="0.18" />
      <rect x="40" y="22" width="22" height="30" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.3" />
      <rect x="64" y="30" width="18" height="22" rx="1.5" fill="currentColor" opacity="0.12" />
      <rect x="64" y="30" width="18" height="22" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.3" />
      <line x1="68" y1="30" x2="68" y2="52" stroke="currentColor" strokeWidth="1.1" opacity="0.28" />

      {/* box opening — the interior, darkest face */}
      <polygon
        points={pts([
          [bx, by],
          [bx2, by],
          [bx2 + ox, by + oy],
          [bx + ox, by + oy],
        ])}
        fill="currentColor"
        opacity="0.2"
      />
      {/* right side face */}
      <polygon
        points={pts([
          [bx2, by],
          [bx2 + ox, by + oy],
          [bx2 + ox, by2 + oy],
          [bx2, by2],
        ])}
        fill="currentColor"
        opacity="0.06"
      />
      {/* front face + kit label */}
      <rect x={bx} y={by} width={bw} height={bh} fill="currentColor" opacity="0.1" />
      <rect x={bx + 16} y={by + 13} width={bw - 32} height="15" rx="2" fill="currentColor" opacity="0.16" />
      <rect x={bx + 22} y={by + 18} width={bw - 44} height="4" rx="2" fill="currentColor" opacity="0.24" />

      {/* silhouette */}
      <polygon
        points={pts([
          [bx, by2],
          [bx, by],
          [bx + ox, by + oy],
          [bx2 + ox, by + oy],
          [bx2 + ox, by2 + oy],
          [bx2, by2],
        ])}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
        opacity="0.34"
      />
      <line x1={bx} y1={by} x2={bx2} y2={by} stroke="currentColor" strokeWidth="1.3" opacity="0.34" />
      <line x1={bx2} y1={by} x2={bx2} y2={by2} stroke="currentColor" strokeWidth="1" opacity="0.3" />
    </svg>
  );
}

function ClosedCarton(): JSX.Element {
  // A sealed carton on the same oblique projection as KitCarton, its lid closed
  // and taped, so packaging ordered as the product never reads as a kit being packed.
  const bx = 22;
  const by = 46;
  const bw = 70;
  const bh = 50;
  const { ox, oy } = offset(20);
  const bx2 = bx + bw;
  const by2 = by + bh;

  return (
    <svg viewBox="0 0 120 124" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx={bx + bw / 2 + 6} cy={by2 + 7} rx={bw * 0.6} ry="6" fill="currentColor" opacity="0.13" />
      {/* lid */}
      <polygon
        points={pts([
          [bx, by],
          [bx2, by],
          [bx2 + ox, by + oy],
          [bx + ox, by + oy],
        ])}
        fill="currentColor"
        opacity="0.14"
      />
      {/* tape along the lid seam */}
      <line x1={bx + bw / 2} y1={by} x2={bx + bw / 2 + ox} y2={by + oy} stroke="currentColor" strokeWidth="3" opacity="0.22" />
      {/* right side face */}
      <polygon
        points={pts([
          [bx2, by],
          [bx2 + ox, by + oy],
          [bx2 + ox, by2 + oy],
          [bx2, by2],
        ])}
        fill="currentColor"
        opacity="0.06"
      />
      {/* front face */}
      <rect x={bx} y={by} width={bw} height={bh} fill="currentColor" opacity="0.1" />
      {/* the tape's run down the front, over the face fill so it matches the lid tape */}
      <line x1={bx + bw / 2} y1={by} x2={bx + bw / 2} y2={by + 14} stroke="currentColor" strokeWidth="3" opacity="0.22" />

      {/* silhouette */}
      <polygon
        points={pts([
          [bx, by2],
          [bx, by],
          [bx + ox, by + oy],
          [bx2 + ox, by + oy],
          [bx2 + ox, by2 + oy],
          [bx2, by2],
        ])}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
        opacity="0.34"
      />
      <line x1={bx} y1={by} x2={bx2} y2={by} stroke="currentColor" strokeWidth="1.3" opacity="0.34" />
      <line x1={bx2} y1={by} x2={bx2} y2={by2} stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <line x1={bx2} y1={by} x2={bx2 + ox} y2={by + oy} stroke="currentColor" strokeWidth="1" opacity="0.3" />
    </svg>
  );
}

// Panel geometry for the jacket, laid flat: back | spine | front, left→right,
// the same wrap order the cover upload uses (COMPONENT_ROLE_HELP.cover).
const JX = 18;
const JY = 34;
const JW = 84;
const JH = 62;

function DustJacketWrap({ artworkUrl }: { artworkUrl?: string | null }): JSX.Element {
  const base = useId();
  const clipId = `${base}-jacket`;
  const x2 = JX + JW;
  const y2 = JY + JH;
  const spineL = JX + 34;
  const spineR = JX + 48;
  const hasArt = Boolean(artworkUrl);

  return (
    <svg viewBox="0 0 120 124" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id={clipId}>
          <rect x={JX} y={JY} width={JW} height={JH} />
        </clipPath>
      </defs>
      <ellipse cx={JX + JW / 2} cy={y2 + 8} rx={JW * 0.5} ry="5.5" fill="currentColor" opacity="0.12" />

      {/* tuck-in flaps folded back behind each end */}
      <polygon points={pts([[JX, JY + 4], [JX - 12, JY + 8], [JX - 12, y2 - 8], [JX, y2 - 4]])} fill="currentColor" opacity="0.07" />
      <polygon points={pts([[JX, JY + 4], [JX - 12, JY + 8], [JX - 12, y2 - 8], [JX, y2 - 4]])} fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" opacity="0.3" />
      <polygon points={pts([[x2, JY + 4], [x2 + 12, JY + 8], [x2 + 12, y2 - 8], [x2, y2 - 4]])} fill="currentColor" opacity="0.07" />
      <polygon points={pts([[x2, JY + 4], [x2 + 12, JY + 8], [x2 + 12, y2 - 8], [x2, y2 - 4]])} fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" opacity="0.3" />

      {/* the printed wrap — live art when supplied, else a branded placeholder
          on the front (right-hand) panel */}
      {hasArt ? (
        <image
          href={artworkUrl as string}
          x={JX}
          y={JY}
          width={JW}
          height={JH}
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${clipId})`}
        />
      ) : (
        <>
          <rect x={JX} y={JY} width={JW} height={JH} fill="currentColor" opacity="0.1" />
          <circle cx={(spineR + x2) / 2} cy={JY + 22} r="8" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.28" />
          <rect x={spineR + 5} y={JY + 33} width={x2 - spineR - 10} height="4" rx="2" fill="currentColor" opacity="0.24" />
          <rect x={spineR + 9} y={JY + 42} width={x2 - spineR - 18} height="4" rx="2" fill="currentColor" opacity="0.2" />
        </>
      )}

      {/* spine panel + the two folds that make it a wrap rather than a sheet */}
      <rect x={spineL} y={JY} width={spineR - spineL} height={JH} fill="currentColor" opacity="0.08" />
      <line x1={spineL} y1={JY} x2={spineL} y2={y2} stroke="currentColor" strokeWidth="1.1" opacity="0.34" />
      <line x1={spineR} y1={JY} x2={spineR} y2={y2} stroke="currentColor" strokeWidth="1.1" opacity="0.34" />
      <rect x={JX} y={JY} width={JW} height={JH} fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.34" />
    </svg>
  );
}

// Map each product family to its book binding (or null for non-book families).
const BINDING_BY_FAMILY: Partial<Record<FamilyCode, BookBinding>> = {
  perfect_bound_book: 'perfect',
  // Hard-bound reuses the glued-spine ("perfect") book visual — the mockup is a
  // small product silhouette, and a case-bound book reads the same at that size.
  hard_bound_book: 'perfect',
  saddle_stitch_book: 'saddle',
  coil_or_wire_o_book: 'coil',
  loose_leaf: 'loose',
};

export function ProjectMockup({
  family,
  artworkUrl,
  sheetAspect,
  mediaType,
  sheetUse,
}: {
  family: FamilyCode | null;
  // Live customer artwork (from useArtworkPreview) — painted on the book cover
  // for booklet families and on the flat sheet for single sheet. Ignored by the
  // label/media families, which have no paint-on face.
  artworkUrl?: string | null;
  // Single-sheet finished-size aspect ratio (width / height); ignored by the
  // other families. Drives the sheet's shape so the thumbnail matches the trim.
  sheetAspect?: number;
  // Media replication covers both optical discs and USB drives; the chosen
  // `media_type` decides which object the thumbnail shows. Ignored by every
  // other family.
  mediaType?: string | null;
  // Portal-only `sheet_use` marker (SchemaForm's PRODUCT_PRESETS). A dust jacket
  // prices as a single_sheet, so the family alone can't tell the two apart —
  // without this the rail draws a plain flat sheet for a jacket.
  sheetUse?: string | null;
}): JSX.Element {
  if (family === 'labels') return <LabelSheet />;
  if (family === 'assembly') {
    return artworkUrl ? <img className="spec-summary__photo" src={artworkUrl} alt="" /> : <KitCarton />;
  }
  if (family === 'custom_packaging') return <ClosedCarton />;
  if (family === 'media_replication') {
    return mediaType === 'usb' ? <UsbDrive /> : <OpticalDisc />;
  }
  if (family === 'single_sheet' && sheetUse === 'dust_jacket') {
    return <DustJacketWrap artworkUrl={artworkUrl} />;
  }
  const binding = family ? BINDING_BY_FAMILY[family] : undefined;
  if (binding) return <Book binding={binding} coverImageUrl={artworkUrl} />;
  // single_sheet, plus the no-selection / unknown fallback.
  return <SingleSheet artworkUrl={artworkUrl} aspect={sheetAspect} />;
}
